import { formatCurrency, formatDate } from '../helpers/formatters';
import AmountWithFee from '../components/AmountWithFee';
import TransactionInfo from '../components/TransactionInfo';
import { useTranslation } from 'react-i18next';

function ReservationOrders({ order, paymentMethods }: { order: any; paymentMethods: any[] }) {
    const { t } = useTranslation();

    // Aggregate signed processing fees from finished transactions so a refund
    // order's amount cell reads "-$100" / "Total: -$103.30" instead of hiding
    // the fee on the transactions sub-table alone.
    const orderFeeSum = (order.transactions || []).reduce(
        (sum: number, tx: any) => (tx.finished ? sum + (tx.processingFee || 0) : sum),
        0,
    );

    return (
        <>
            {/* Orders */}
            <div className="fireknife-reservation-orders">
                <h2 className="fireknife-reservation-orders__title">
                    {t('widgetReservationInfo.orders.title')}
                </h2>

                <table className="fireknife-reservation-orders__order-data-table">
                    <thead>
                        <tr>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.orders.orderId')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.orders.status')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.orders.createdAt')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.orders.rollbackTimeAt')}
                            </th>
                            <th className="fireknife-text-end">
                                {t('widgetReservationInfo.orders.amount')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr key={order.id}>
                            <th
                                className="fireknife-text-start"
                                aria-label={t('widgetReservationInfo.orders.orderId')}
                            >
                                #{order.id}
                            </th>
                            <td
                                className="fireknife-text-start"
                                arial-label={t('widgetReservationInfo.orders.status')}
                            >
                                {t('widgetReservationInfo.orders.statusValues.' + order?.status)}
                            </td>
                            <td
                                className="fireknife-text-start"
                                aria-label={t('widgetReservationInfo.orders.createdAt')}
                            >
                                {formatDate(order.createdAt, t('widgetLocale'))}
                            </td>
                            <td
                                className="fireknife-text-start"
                                aria-label={t('widgetReservationInfo.orders.rollbackTimeAt')}
                            >
                                {formatDate(order.rollbackTimeAt, t('widgetLocale'))}
                            </td>
                            <td
                                className="fireknife-text-end"
                                aria-label={t('widgetReservationInfo.orders.amount')}
                            >
                                {formatCurrency(order.amount / 100)}
                                <AmountWithFee
                                    amount={order.amount}
                                    fee={orderFeeSum}
                                    discount={order.discountAmount || 0}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                {order.transactions.length > 0 && (
                    <div className="fireknife-transactions">
                        <h2 className="fireknife-transactions__title">{t('transactions.title')}</h2>
                        <div className="fireknife-transactions__list">
                            <table className="fireknife-transactions__table">
                                <thead>
                                    <tr>
                                        <th className="fireknife-text-start">
                                            {t('widgetReservationInfo.transaction.transactionId')}
                                        </th>
                                        <th className="fireknife-text-start">
                                            {t('widgetReservationInfo.transaction.status')}
                                        </th>
                                        <th className="fireknife-text-start">
                                            {t('widgetReservationInfo.transaction.paymentMethod')}
                                        </th>
                                        <th className="fireknife-text-start">
                                            {t('widgetReservationInfo.transaction.date')}
                                        </th>
                                        <th className="fireknife-text-end">
                                            {t('widgetReservationInfo.transaction.amount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.transactions.map((transaction) => (
                                        <TransactionInfo
                                            key={transaction.id}
                                            transaction={transaction}
                                            paymentMethod={paymentMethods.find(
                                                (pm) => pm.slug === transaction.paymentType,
                                            )}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default ReservationOrders;
